import React from 'react';
import { ButtonV2, Flex, TextV2 } from '@withjoy/joykit';
import { pxToRem } from '@withjoy/joykit/theme';
import { useTranslation } from '@shared/core';
import { useResponsive } from '@shared/utils/hooks/useResponsive';
import { ResponsiveDialog } from '@shared/components/ResponsiveDialog';
import { closeButtonOverridesV2, dialogOverridesV2, drawerOverridesV2 } from '@shared/components/ResponsiveDialog/ResponsiveDialog.styles';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { useHotelBookingGuestSiteTelemetry } from '../../HotelBooking.telemetry';
import { TravelCurrencyCode } from '@graphql/generated';

interface RedirectInterceptorDialogProps {
  isOpen: boolean;
  hotel?: {
    hotelName?: string | null;
    hotelPriceCurrency?: TravelCurrencyCode | null;
    hotelPrice?: string | null;
    hotelStrikeOutPrice?: string | null;
  };
  redirectUrls?: {
    partnerHotel: string;
    partnerSearch: string;
  };
  onDecline: () => void;
  onAccept: () => void;
}

export const RedirectInterceptorDialog = ({ isOpen, hotel, onAccept: onAcceptCallback, onDecline: onDeclineCallback, redirectUrls }: RedirectInterceptorDialogProps) => {
  const { t } = useTranslation('guestSiteTravel');
  const interceptorRedirectDialogTrans = t('interceptorRedirectDialog');
  const [isMobile] = useResponsive({ values: { mobile: true, tablet: false } });
  const telemetry = useHotelBookingGuestSiteTelemetry();

  const onAccept = useEventCallback(() => {
    telemetry.partnerHotelRedirectInterceptorDialog({ dialogAction: 'accepted', ...hotel });
    onAcceptCallback();
  });

  const onDecline = useEventCallback(() => {
    telemetry.partnerHotelRedirectInterceptorDialog({ dialogAction: 'declined', ...hotel });
    onDeclineCallback();
  });

  return (
    <ResponsiveDialog
      dialogOverrides={{ ...dialogOverridesV2, Content: { props: { maxWidth: pxToRem(540) } }, Footer: { props: { paddingX: 80 } } }}
      drawerOverrides={drawerOverridesV2}
      closeButtonOverrides={closeButtonOverridesV2}
      isOpen={isOpen}
      hasCloseButton={!isMobile}
      onClose={onDecline}
      hideDivider={true}
      removeBodyPadding={true}
      hideFooterDivider={!isMobile}
      disableCloseOnEscapeKeyClick
      disableCloseOnOutsideClick
      renderFooter={() => {
        return (
          <Flex flexDirection="column" width="100%" gap={5} justifyContent="center">
            <ButtonV2
              typographyVariant="button2"
              width="100%"
              shape="rounded"
              intent="neutral"
              onClick={onAccept}
              {...(redirectUrls && { as: 'a', href: redirectUrls.partnerSearch, target: '_blank' })}
            >
              {interceptorRedirectDialogTrans.acceptCta()}
            </ButtonV2>
            <ButtonV2
              typographyVariant="button2"
              width="100%"
              color="mono14"
              variant="ghost"
              onClick={onDecline}
              {...(redirectUrls && { as: 'a', href: redirectUrls.partnerHotel, target: '_blank' })}
            >
              {interceptorRedirectDialogTrans.declineCta()}
            </ButtonV2>
          </Flex>
        );
      }}
    >
      <Flex flexDirection={'column'} textAlign={'center'} paddingX={{ _: 6, sm2: 8 }} paddingTop={{ _: 7, sm2: 9 }} paddingBottom={{ _: 7, sm2: 5 }}>
        <TextV2 typographyVariant={'hed4'} color={'#020202'}>
          {interceptorRedirectDialogTrans.title({ hotelName: hotel?.hotelName ?? 'Hotel' })}
        </TextV2>
        <TextV2 typographyVariant={'body1'} color={'mono10'} marginTop={3} marginBottom={{ _: 6, sm2: 0 }}>
          {interceptorRedirectDialogTrans.content()}
        </TextV2>
      </Flex>
    </ResponsiveDialog>
  );
};
